import React, { useEffect, useRef, useState } from 'react';

import { _t } from '@/locale';

import { classnames } from '@/helpers';

type TextAreaProps = {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  characterLimit?: number;
};

const TextArea = ({ value, onChange, placeholder, rows = 4, disabled, autoFocus, characterLimit }: TextAreaProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (autoFocus && textAreaRef.current) {
      textAreaRef.current.focus();
      setIsFocused(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus]);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    if (!textAreaRef.current?.value) {
      setIsFocused(false);
    }
  };

  return (
    <div className="relative w-full">
      <textarea
        id="textarea"
        ref={textAreaRef}
        value={value}
        onChange={(event) => {
          if (characterLimit && event.target.value.length > characterLimit) {
            return;
          }

          onChange(event);
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        rows={rows}
        disabled={disabled}
        autoFocus={autoFocus}
        className={classnames(
          'pb-xs pt-xl px-sm placeholder:text-l border-border_regular focus-visible:outline-border_focus w-full rounded-sm border',
          {
            'border-border_disabled': disabled,
          },
        )}
      />
      {placeholder && (
        <label
          htmlFor="textarea"
          className={classnames('left-sm top-sm absolute transition-all duration-200 ease-in-out', {
            'text-fg_disabled': disabled,
            'text-fg_secondary cursor-text': !disabled,
            'text-xs': isFocused || value,
            'text-l': !isFocused && !value,
          })}
          style={{
            transform: isFocused || value ? 'translateY(0) translateX(0)' : 'translateY(0) translateX(0)',
          }}>
          {placeholder}
        </label>
      )}
      {characterLimit ? (
        <p className="text-fg_secondary text-right text-xs">
          {_t('AmountOfCharactersLeft', { count: characterLimit - value.length })}
        </p>
      ) : null}
    </div>
  );
};

export default TextArea;
